import { isHighDpiDevice } from '../../utils/device.js';

import type { ImagePreset } from './presets';

// TODO: update transforms for categories slider (changed items count on 25.08)
// TODO: add separate transforms for filtered list

/*
When adding new transforms:
1. check the intrinsic width of an element in these viewports:
  - 320x568 (iPhone 5)
  - 375x667 (iPhone 6/7/8)
  - 667x375 (iPhone 6/7/8 landscape)
  - 600x1000 (transient mobile/tablet)
  - 768x1024 (iPad)
  - 1024x768 (iPad landscape)
  - 1440
  - 1680
  - 1920
  - 2560
2. write down all of the widths (rounded up)
3. pick matching transforms from the whitelist at http://svp.vg.no/svp/api/v1/docs/ (IMBO images)
  - try limiting to 5-7 transforms in total
  - remember to pick the closest higher or equal width value (so that it kicks in using sizes)
 */

const imagesTransforms: Record<ImagePreset, string[]> = !isHighDpiDevice
  ? {
      splashBackground: [
        '375x211q80 375w',
        '600x337q80 600w',
        '768x432q80 768w',
        '1024x576q80 1024w',
        '1440x810q80 1440w',
        '1680x945q80 1680w',
        '1920x1080q80 1920w',
      ],
      splashIcon: ['71x71q80 71w', '87x87q80 87w', '160x160q80 160w'],
      editorialSingle: [
        '375x211q80 375w',
        '600x337q80 600w',
        '768x432q80 768w',
        '1024x576q80 1024w',
      ],
      editorialProfile: ['87x87q80 87w', '160x160q80 160w', '220x220q80 220w'],
      categoryLink: ['80x45q80 80w', '102x57q80 102w'],
      assetsSlider: [
        '193x109q80 193w',
        '225x127q80 225w',
        '281x158q80 281w',
        '338x190q80 338w',
        '382x215q80 382w',
        '421x237q80 421w',
      ],
      assetsSliderVertical: [
        'c96x144q80 96w',
        'c116x174q80 116w',
        'c130x195q80 130w',
        'c148x222q80 148w',
        'c196x294q80 196w',
        'c214x321q80 214w',
        'c258x387q80 258w',
      ],
      categoriesSlider: [
        '124x70q80 124w',
        '142x80q80 142w',
        '281x158q80 281w',
        '338x190q80 338w',
        '382x215q80 382w',
        '421x237q80 421w',
      ],
      profilesSlider: [
        '71x71q80 71w',
        '87x87q80 87w',
        '160x160q80 160w',
        '220x220q80 220w',
        '288x288q80 288w',
      ],
      editorialSlider: [
        '161x91q80 161w',
        '193x109q80 193w',
        '199x112q80 199w',
        '287x161q80 161w',
        '320x180q80 320w',
      ],
      editorialSliderVertical: [
        'c96x144q80 96w',
        'c116x174q80 116w',
        'c130x195q80 130w',
        'c148x222q80 148w',
        'c196x294q80 196w',
        'c214x321q80 214w',
      ],
      assetsListVertical: [
        '142x80q80 142w',
        '174x98q80 174w',
        '235x132q80 235w',
        '289x163q80 289w',
        '382x215q80 382w',
      ],
      assetsListHorizontal: [
        '142x80q80 142w',
        '161x91q80 161w',
        '193x109q80 193w',
        '225x127q80 225w',
        '276x155q80 276w',
      ],
      assetsFilteredList: [
        '124x70q80 124w',
        '161x91q80 161w',
        '203x114q80 203w',
        '220x124q80 220w',
        '276x155q80 276w',
        '359x202q80 359w',
      ],
      assetsFilteredListVertical: [
        'c96x144q80 96w',
        'c148x222q80 148w',
        'c196x294q80 196w',
        'c214x321q80 214w',
        'c258x387q80 258w',
      ],
      categoriesList: [],
    }
  : {
      splashBackground: [
        '750x422q50 750w',
        '1200x675q50 1200w',
        '1536x864q50 1536w',
        '2048x1152q50 2048w',
        '2880x1620q50 2880w',
        '3360x1890q50 3360w',
        '3840x2160q50 3840w',
      ],
      splashIcon: ['142x142q50 142w', '174x174q50 174w', '320x320q50 320w'],
      editorialSingle: [
        '750x422q50 750w',
        '1200x675q50 1200w',
        '1536x864q50 1536w',
        '2048x1152q50 2048w',
      ],
      editorialProfile: [
        '174x174q50 174w',
        '320x320q50 320w',
        '440x440q50 440w',
      ],
      categoryLink: ['160x90q50 160w', '204x114q50 204w'],
      assetsSlider: [
        '386x218q50 386w',
        '450x254q50 450w',
        '562x316q50 562w',
        '676x380q50 676w',
        '764x430q50 764w',
        '842x474q50 842w',
      ],
      assetsSliderVertical: [
        'c192x288q50 192w',
        'c232x348q50 232w',
        'c260x390q50 260w',
        'c296x444q50 296w',
        'c392x588q50 392w',
        'c428x642q50 428w',
        'c516x774q50 516w',
      ],
      categoriesSlider: [
        '248x140q50 248w',
        '284x160q50 284w',
        '562x316q50 562w',
        '676x380q50 676w',
        '764x430q50 764w',
        '842x474q50 842w',
      ],
      profilesSlider: [
        '142x142q50 142w',
        '174x174q50 174w',
        '320x320q50 320w',
        '440x440q50 440w',
        '576x576q50 576w',
      ],
      editorialSlider: [
        '322x182q50 322w',
        '386x218q50 386w',
        '398x224q50 398w',
        '574x322q50 574w',
        '640x360q50 640w',
      ],
      editorialSliderVertical: [
        'c192x288q50 192w',
        'c232x348q50 232w',
        'c260x390q50 260w',
        'c296x444q50 296w',
        'c392x588q50 392w',
        'c428x642q50 428w',
      ],
      assetsListVertical: [
        '284x160q50 284w',
        '348x196q50 348w',
        '470x264q50 470w',
        '578x326q50 578w',
        '764x430q50 764w',
      ],
      assetsListHorizontal: [
        '284x160q50 284w',
        '322x182q50 322w',
        '386x218q50 386w',
        '450x254q50 450w',
        '552x310q50 552w',
      ],
      assetsFilteredList: [
        '248x140q50 248w',
        '322x182q50 322w',
        '406x228q50 406w',
        '440x248q50 440w',
        '552x310q50 552w',
        '718x404q50 718w',
      ],
      assetsFilteredListVertical: [
        'c192x288q50 192w',
        'c296x444q50 296w',
        'c392x588q50 392w',
        'c428x642q50 428w',
        'c516x774q50 516w',
      ],
      categoriesList: [],
    };

imagesTransforms.categoriesList = imagesTransforms.assetsListVertical;

const defaultImagesTransforms: Record<ImagePreset, string> = {
  splashIcon: '87x87q80',
  splashBackground: '1440x810q80',
  editorialSingle: '1024x576q80',
  editorialProfile: '220x220q80',
  categoryLink: '102x57q80',
  assetsSlider: '338x190q80',
  assetsSliderVertical: 'c258x387q80',
  categoriesSlider: '338x190q80',
  profilesSlider: '220x220q80',
  editorialSlider: '398x224q50',
  editorialSliderVertical: 'c148x222q80',
  assetsListVertical: '235x132q80',
  categoriesList: '235x132q80',
  assetsListHorizontal: '225x127q80',
  assetsFilteredList: '276x155q80',
  assetsFilteredListVertical: 'c196x294q80',
};

export { imagesTransforms, defaultImagesTransforms };
